import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import NumberFlow from "@number-flow/react"
import { Alert, App, Card, Col, Flex, Row, Space, Spin, Table, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import { getCompareParams } from "../../Components/Filters/CompareSelect/presets"
import { DashboardFiltersBlock } from "../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import DropdownBlockCheckbox from "../../Components/Filters/DropdownBlockCheckbox"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import Private from "../../Components/Private"
import TourComponent from "../../Components/Tour"
import { useAnimatedNumber } from "../../hooks/useAnimatedNumber"
import Icon from "../../Icon"
import Images from "../../Images"
import { modalConfig } from "../../theme"
import { getDates, getTourEl, useQuery } from "../../Utils/utils"
import BusinessAccounts from "../BusinessAccountsPage/reducer"
import AuthCreators from "../LoginPage/reducer"
import { useBalanceTopUpEvent } from "../PersonalSettings/hooks/useBalanceTopUpEvent"
import { useDemoDataToggle } from "../User/hooks/useDemoDataToggle"
import UserCreators from "../User/reducer"
import AlertsAccountChart from "./components/AlertsAccountChart"
import AlertsCountryChart from "./components/AlertsCountryChart"
import AlertsDescriptorChart from "./components/AlertsDescriptorChart"
import AlertsOverviewChart from "./components/AlertsOverviewChart"
import { CardTotalPanel } from "./components/CardTotalPanel"
import CreditsSection from "./components/CreditsSection"
import KpiSection, { hasRequiredKpiBlocks } from "./components/KpiSection"
import SankeySection from "./components/SankeySection"
import {
  alertsByBankColumns,
  alertsByCardTypeColumns,
  getDashboardProgressRowProps
  // usersBySourceTypeColumns,
} from "./components/Table/columns"
import Creators from "./reducer"
import { hasBillingDescriptors, hasEmptyVisaData, hasVisaData } from "./utils/helpers"

import "./styles.less"

const { Title, Text, Link } = Typography

const statusesForPendingText = ["pending", "needs_help"]

// Every "Alerts by ..." card is the same height, whatever it holds. They sit two to a row,
// and a taller neighbour used to leave the shorter one floating. `height: 100%` alone would
// only equalise within a row; pinning the same minimum keeps them equal across rows too.
const ALERTS_CARD_HEIGHT = 704

export const DashboardPage = ({
  usersViewForAdmin,
  nav: { theme, isMobile },
  isAdmin,
  userInfo,
  showModalPremiumFeatures,
  dashboard,
  inviteInfo,
  businessAccountsWithRole,
  businessAccount,

  test_data,
  balanceEnabled,
  demoTourStep,
  demoRef,
  statisticBlocksData,
  statisticLabels,
  statisticsFetching,
  creditsSummaryData,
  creditsSummaryFetching,
  alertsFlowData,
  enrollments,
  ...props
}) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  let query = useQuery(search)
  let business_account_id = query.get("business_account_id")
  let inviteId = query.get("invite")
  useBalanceTopUpEvent()
  const { t } = useTranslation()
  const helpRef = useRef({ firstLoading: true })
  const topUpBalanceRef = useRef(null)
  const { modal } = App.useApp()
  const adminWithUserView = isAdmin && usersViewForAdmin
  const isAdminPage = isAdmin && !usersViewForAdmin

  const {
    nearest_enrollment_connection,
    current_business_account_id,
    is_active,
    is_demo,
    is_light_mode
  } = userInfo

  const businessId = adminWithUserView ? business_account_id : current_business_account_id
  const inactiveByStatus = businessAccount?.status === "inactivated"
  const isAccountPending = nearest_enrollment_connection && !isAdmin && !is_demo
  const shouldShowPendingText = statusesForPendingText.includes(businessAccount?.status)
  const showPendingText =
    (shouldShowPendingText && !isAdmin) || (usersViewForAdmin && shouldShowPendingText)
  const isDemo = adminWithUserView ? businessAccount?.["is_demo"] : is_demo
  const isLightMode = adminWithUserView ? false : is_light_mode
  const showManageBillingBtn = !usersViewForAdmin && userInfo?.ba_role?.id === 1

  const hasDescriptors = hasBillingDescriptors(enrollments)
  const hasRdrCodes = hasVisaData(enrollments)
  const hasNoRdrCodes = hasEmptyVisaData(enrollments)

  const isHelp = businessAccount?.status === "needs_help"
  const isPending = businessAccount?.status === "pending"
  const canShowDashboardBanner = balanceEnabled && !isAdmin

  const shouldShowPendingEnrollmentBanner =
    isPending && hasDescriptors && !isDemo && hasRdrCodes && canShowDashboardBanner && !isAdmin

  const shouldShowMissingEnrollmentDataBanner =
    isHelp && canShowDashboardBanner && !isDemo && !hasDescriptors && hasNoRdrCodes && !isAdmin

  const shouldShowMissingBillingDescriptorBanner =
    isHelp && canShowDashboardBanner && !isDemo && !hasDescriptors && hasRdrCodes && !isAdmin

  const shouldShowMissingRdrCodesBanner =
    isPending && canShowDashboardBanner && !isDemo && hasDescriptors && hasNoRdrCodes && !isAdmin

  // const remainingTime = getRemainingTime(nearest_enrollment_connection)

  // const alertText = remainingTime?.days < 0
  //   ? t('Your account is currently pending, it may take some time to get your account setup for alerts.')
  //   : t("Your account is currently pending, it may take up to _time_ _unit_ to get your account setup for alerts.",
  //     {
  //       time: remainingTime?.days ? (remainingTime?.days + 1) : remainingTime?.hours,
  //       unit: t(remainingTime?.days ? (remainingTime?.days === 1 ? 'day' : 'days') : 'hours')
  //     }
  //   )

  const alertText = t(
    "Your account is currently pending, it may take up to _time_ _unit_ to get your account setup for alerts.",
    {
      time: 48,
      unit: "hours"
    }
  )
  const alertBillingText = t(
    "Add a billing descriptor to use alerts. Need help? Support will email you within _time_ _unit_.",
    {
      time: 48,
      unit: "hours"
    }
  )

  const {
    filters,
    alertsOverviewFetching,
    alertsOverviewData,
    alertsByDescriptorData,
    alertsByAccountData,
    alertsCountryData,
    alertsBankData,
    alertsCardTypeData,
    // usersBySourceData,
    allBusinessAccountsList
  } = dashboard

  //filters in redux dashboard state filters: {period, alertsType, }
  //period: '7 days', '30 days', 'all', 'custom'
  //alertsType: 'volume', 'count'
  //descriptors: 'all', chosen in dropdown
  const descriptor = filters?.descriptors === "all" ? null : filters?.descriptors?.toString()
  const business_account =
    filters?.business_accounts === "all" ? null : filters?.business_accounts?.toString()

  const animatedOverviewTotal = useAnimatedNumber(alertsOverviewData?.total)

  // Which metric the chart under the KPI tabs is drawing. Total alerts opens the page,
  // the way the alerts overview used to.
  const [selectedKpi, setSelectedKpi] = useState("total_alerts")

  const adminParams = isAdmin && usersViewForAdmin ? { business_account_id: businessId } : {}

  // The comparison period totals the "compared with" calendar returns, unpacked onto the
  // CardTotalPanel props the breakdown cards read. Null when comparison is off.
  const compareProps = (d) => ({
    compareCount: d?.comparison_total_count,
    compareVolume: d?.comparison_total_volume,
    compareLabel: d?.comparison_period?.label
  })

  const handleChangeFilter = useCallback(
    (newFilters) => {
      if (isAccountPending) return
      if (Array.isArray(newFilters.period)) {
        props.changeFilters({
          ...newFilters,
          period: { type: "custom", period: newFilters.period }
        })
      } else {
        props.changeFilters(newFilters)
      }
    },
    [isAccountPending]
  )

  const handleChangeFilterDescriptors = useCallback((newFilters) => {
    props.changeFilters({ descriptors: newFilters?.["descriptors"] })
  }, [])

  const handleChangeFilterAccounts = useCallback((newFilters) => {
    props.changeFilters({
      business_accounts: newFilters?.["business_accounts"]
    })
  }, [])

  const { isShowDemoData } = useDemoDataToggle()

  useEffect(() => {
    if (businessId) {
      props.getEnrollments(businessId)
    }
  }, [businessId])

  useEffect(() => {
    document.title = "Dashboard - Chargeback"
    if (isAdmin && !usersViewForAdmin) {
      props.getAllBusinessAccountsList()
      return
    }
    // With a business account the backend returns that account's stats. During
    // onboarding (no business account) it returns demo stats when demo mode is on
    // and zeros when it is off, so the cards stay in sync with the demo toggle.
    // The blocks carry the series the chart below them draws, so they move with the
    // calendar like every other chart on the page.
    // businessId is the viewed account: the admin's own for a merchant, or the
    // ?business_account_id the admin drilled into (adminWithUserView), so both paths scope right.
    props.getDashboardStatistics({
      ...getDates(filters.period),
      ...(businessId ? { business_account_id: businessId } : {})
    })
    // The credits card is scoped by the same calendar. Admins have no credits card.
    if (!isAdminPage) {
      props.getCreditsSummary({
        chartType: "credits",
        ...getDates(filters.period),
        ...(businessId ? { business_account_id: businessId } : {})
      })
    }
  }, [businessId, filters?.period, isDemo, isShowDemoData])

  // The Sankey needs the joint source x outcome distribution - aggregated server-side
  // (CHA1-2007), so this just forwards the same period/BA scoping every other chart uses.
  useEffect(() => {
    if (isAdminPage) return
    const period_query = getDates(filters.period)
    props.getAlertsFlow({
      ...period_query,
      ...(businessId ? { business_account_id: businessId } : {})
    })
  }, [filters?.period, businessId, isDemo, isShowDemoData])

  useEffect(() => {
    // Only the admin dashboard still draws the alerts overview. The merchant page draws
    // the selected KPI block's series instead, so it has no count/volume switch to react
    // to and nothing to fetch here.
    if (isAdminPage && filters.alertsType && !helpRef.current.firstLoading) {
      const period_query = getDates(filters.period)
      props.getAlertOverviewData({
        chartType: "overview",
        overview_by: filters.alertsType,
        ...period_query,
        ...adminParams,
        ...(isAdminPage ? { test_data } : {})
      })
    }
  }, [filters.alertsType, test_data, isShowDemoData])

  useEffect(() => {
    if (filters?.descriptors && !helpRef.current.firstLoading) {
      if (isAdminPage) return
      const period_query = getDates(filters.period)
      props.getAlertsDescriptorData({
        chartType: "by_descriptor",
        descriptor,
        ...period_query,
        ...adminParams,
        ...(isAdminPage ? { test_data } : {})
      })
    }
  }, [filters?.descriptors, test_data, isShowDemoData])

  useEffect(() => {
    if (
      filters?.business_accounts &&
      !helpRef.current.firstLoading &&
      isAdmin &&
      !usersViewForAdmin
    ) {
      const period_query = getDates(filters.period)
      props.getAlertsAccountData({
        chartType: "by_business_account",
        business_account,
        ...period_query,
        ...adminParams,
        ...(isAdminPage ? { test_data } : {})
      })
    }
  }, [filters?.business_accounts, test_data, isShowDemoData])

  useEffect(() => {
    if (filters?.period) {
      const period_query = getDates(filters.period)
      const compare_query = getCompareParams(filters?.compare, period_query)
      if (isAdminPage) {
        props.getAlertOverviewData({
          chartType: "overview",
          overview_by: filters.alertsType,
          ...period_query,
          ...compare_query,
          ...adminParams,
          test_data
        })
      }
      props.getAlertsCountryData({
        chartType: "by_country",
        limit: 13,
        ...period_query,
        ...compare_query,
        ...adminParams,
        ...(isAdminPage ? { test_data } : {})
      })

      props.getAlertsBankData({
        chartType: "by_bank",
        limit: 9,
        ...period_query,
        ...compare_query,
        ...adminParams,
        ...(isAdminPage ? { test_data } : {})
      })
      props.getAlertsCardTypeData({
        chartType: "by_card_type",
        ...period_query,
        ...compare_query,
        ...adminParams,
        ...(isAdminPage ? { test_data } : {})
      })

      if (isAdmin && !usersViewForAdmin) {
        props.getAlertsAccountData({
          chartType: "by_business_account",
          business_account,
          ...period_query,
          ...compare_query,
          ...adminParams,
          test_data
        })
        // props.getUsersBySourceData({
        //   chartType: "users_by_source",
        //   ...period_query,
        //   test_data,
        // });
      } else {
        props.getAlertsDescriptorData({
          chartType: "by_descriptor",
          descriptor,
          ...period_query,
          ...compare_query,
          ...adminParams
        })
      }

      if (helpRef.current.firstLoading) helpRef.current.firstLoading = false
    }
  }, [filters?.period, filters?.compare, businessId, test_data, isShowDemoData])

  useEffect(() => {
    if (showModalPremiumFeatures) {
      modal.confirm({
        ...modalConfig("info"),
        width: 624,
        centered: !isMobile,
        getContainer: () => document.getElementById("main-layout"),

        title: t("Additional services are available"),
        content: (
          <Space size={20} direction={"vertical"}>
            <Text type="secondary">
              {t(
                "You reached 1000+ alerts last month. We are excited to offer you additional paid services to enhance your experience with Chargeback platform."
              )}
            </Text>
            <Text type="secondary">
              {t(
                "Click the ‘Check and connect’ button to view the available features and subscribe to the ones that best meet your needs."
              )}
            </Text>
            <Text type="secondary">
              {t(
                "All purchased services will be displayed on the Integrations page in your business account."
              )}
            </Text>
          </Space>
        ),
        cancelText: t("Cancel"),
        okText: t("Check and connect"),
        cancelButtonProps: { size: "large" },
        okButtonProps: { size: "large" },
        onOk() {
          props.setShowModalPremiumFeatures(false)
          navigate("/integrations/premium_features")
        },
        onCancel() {
          props.declinePremiumFeatures()
          //props.setShowModalPremiumFeatures(false)
        }
      })
    }
  }, [showModalPremiumFeatures])

  const steps6 = useMemo(
    () => [
      {
        title: (
          <Text type="secondary">
            You're currently in Demo Mode. Not all features are available, and data shown is for
            demo only.
          </Text>
        ),
        target: () => getTourEl("demo-mode-banner")
      },
      {
        title: (
          <Text type="secondary">
            Click here to quickly top up your account balance and unlock the platform's full
            potential!
          </Text>
        ),
        target: () => topUpBalanceRef?.current,
        nextButtonProps: {
          onClick: () => {
            navigate("/settings/balance")
            props.setDemoTourStep(7)
          },
          children: <text>Next</text>
        }
      }
    ],
    [demoRef, topUpBalanceRef]
  )

  const [showDemoTourDelayed, setShowDemoTourDelayed] = useState(false)

  useEffect(() => {
    let timer
    if (demoTourStep === 1) {
      timer = setTimeout(() => setShowDemoTourDelayed(true), 500)
    }
    return () => clearTimeout(timer)
  }, [demoTourStep, isDemo])

  const steps1 = useMemo(
    () => [
      {
        title: <Text type="secondary">Jump between your different businesses in one click</Text>,
        target: () => getTourEl("business-switcher")
      },
      {
        title: (
          <Text type="secondary">
            Get a quick and clear view of your business performance, all in one place.
          </Text>
        ),
        target: () => getTourEl("dashboard-nav"),
        nextButtonProps: {
          onClick: () => {
            navigate(`/alerts`)
            props.setDemoTourStep(2)
          },
          children: <text>Next</text>
        }
      }
    ],
    []
  )

  const handleDeleteTooltip = () => {
    const tooltip = document.getElementById("chartjs-tooltip_descriptor")
    if (tooltip && !tooltip?.matches(":hover")) tooltip?.remove()
  }

  useEffect(() => {
    const page = document.getElementById("dashboard-page")
    if (page) page?.addEventListener("mouseover", handleDeleteTooltip)
    return () => page?.removeEventListener("mouseover", handleDeleteTooltip)
  }, [])

  const handleGoToBilling = () => {
    navigate("/settings/balance")
  }

  const handleGoToEnrollment = () => {
    navigate("/configure/enrollment")
  }

  const openFullPage = (chart) => {
    navigate(`${chart}${usersViewForAdmin ? search : ""}`)
  }

  return (
    <div
      id="dashboard-page"
      className={cls("dashboard-page", {
        "dashboard-page_users_view_for_admin": usersViewForAdmin
      })}
    >
      <Row gutter={[32, 32]} align={"stretch"}>
        {/* TODO: will need use */}{" "}
        {/* {showPendingText && !isDemo && !isLightMode && is_active && (
          <Col span={24} style={{ marginBottom: -8 }}>
            <Alert
              type="warning"
              className={"fullscreen-alert"}
              showIcon
              icon={<Icon type="loader" />}
              message={`${t("Status")}: ${alertText}`}
            />
          </Col>
        )} */}
        {((!is_active && !isAdmin) || (inactiveByStatus && usersViewForAdmin)) && (
          <Col span={24} style={{ marginBottom: -8 }}>
            <Alert
              type="error"
              className={"fullscreen-alert"}
              showIcon
              icon={<Icon type={"alert_triangle_error"} />}
              message={
                <>
                  {t(
                    "Your business account has been deactivated due to failed payment attempts. Please check your payment details."
                  )}
                  {showManageBillingBtn && (
                    <Link onClick={handleGoToBilling} strong style={{ marginLeft: 8 }}>
                      {t("Manage Billing")}
                    </Link>
                  )}
                </>
              }
            />
          </Col>
        )}
        {shouldShowPendingEnrollmentBanner && (
          <Col span={24} style={{ marginBottom: -8 }}>
            <Alert
              type="warning"
              className={"fullscreen-alert"}
              showIcon
              icon={<Icon type="loader" />}
              message={
                <>
                  <Text>{t("Your account enrollment is currently pending.")}</Text>{" "}
                  <Link onClick={handleGoToEnrollment}>{t("Track enrollment status")}</Link>
                </>
              }
            />
          </Col>
        )}
        {shouldShowMissingEnrollmentDataBanner && (
          <Col span={24} style={{ marginBottom: -8 }}>
            <Alert
              type="warning"
              className={"fullscreen-alert"}
              showIcon
              icon={<Icon type="loader" />}
              message={
                <>
                  <Text>
                    {t(
                      "Add a billing descriptor to use alerts. Add ARN or BIN + CAID for RDR alerts. Need help? Support will email you within 48 hours."
                    )}
                  </Text>{" "}
                  <Link onClick={handleGoToEnrollment}>{t("Add required information")}</Link>
                </>
              }
            />
          </Col>
        )}
        {shouldShowMissingBillingDescriptorBanner && (
          <Col span={24} style={{ marginBottom: -8 }}>
            <Alert
              type="warning"
              className={"fullscreen-alert"}
              showIcon
              icon={<Icon type="loader" />}
              message={
                <>
                  <Text>
                    {t(
                      "Add a billing descriptor to use alerts. Need help? Support will email you within 48 hours."
                    )}
                  </Text>{" "}
                  <Link onClick={handleGoToEnrollment}>{t("Add descriptor")}</Link>
                </>
              }
            />
          </Col>
        )}
        {shouldShowMissingRdrCodesBanner && (
          <Col span={24} style={{ marginBottom: -8 }}>
            <Alert
              type="warning"
              className={"fullscreen-alert"}
              showIcon
              icon={<Icon type="loader" />}
              message={
                <>
                  <Text>
                    {t(
                      "Add ARNs or BIN + CAID to use RDR alerts. Need help? Support will email you within 48 hours."
                    )}
                  </Text>{" "}
                  <Link onClick={handleGoToEnrollment}>{t("Add RDR codes")}</Link>
                </>
              }
            />
          </Col>
        )}
        {/* The calendar scopes the whole page, so it sits above everything, on its own
            rule. The admin dashboard keeps the count/volume toggle beside it: its chart
            is still the alerts overview, which has no metric tabs to switch. */}
        <Col span={24}>
          <Flex
            justify={isAdminPage ? "flex-end" : "flex-start"}
            align="center"
            gap={12}
            className={cls({ "dashboard-filter-row": !isAdminPage })}
          >
            <DashboardFiltersBlock
              onlyPeriod={!isAdminPage}
              showCompare
              handleChangeFilter={handleChangeFilter}
              filters={filters}
              disabled={isAccountPending}
            />

            <GlobalFilterTestData
              usersViewForAdmin={usersViewForAdmin}
              size={"large"}
              variant={"compact"}
            />
          </Flex>
        </Col>
        {!isAdminPage ? (
          <Col span={24}>
            {statisticLabels?.length && hasRequiredKpiBlocks(statisticBlocksData) ? (
              <KpiSection
                blocks={statisticBlocksData}
                labels={statisticLabels}
                selectedKey={selectedKpi}
                onSelect={setSelectedKpi}
                theme={theme}
                loading={statisticsFetching}
              />
            ) : (
              <Card title="" className="card-chart-wrapper alerts-overview">
                <Spin spinning={statisticsFetching}>
                  <Flex vertical justify="start" align="center" style={{ height: 254 }}>
                    <img
                      src={Images?.[theme === "dark" ? "alerts_overview_dark" : "alerts_overview"]}
                      alt=""
                      width={320}
                    />
                    <Text style={{ fontSize: 16 }}>No data available yet..</Text>
                    <Text
                      type="secondary"
                      style={{
                        textAlign: "center",
                        paddingTop: 8,
                        maxWidth: 500
                      }}
                    >
                      {`${alertText} ${t("You will be notified by email.")}`}
                    </Text>
                  </Flex>
                </Spin>
              </Card>
            )}
          </Col>
        ) : (
          <>
            <Col span={24}>
              <Flex justify={"space-between"} align="flex-start">
                <div>
                  <div>
                    <Text strong style={{ fontSize: 14 }}>{`${t("Alerts overview")} `}</Text>
                    <Text
                      type="secondary"
                      strong
                      style={{ fontSize: 14 }}
                    >{`by ${t(filters?.alertsType)}`}</Text>
                  </div>
                  <Title
                    style={{
                      textAlign: "left",
                      margin: "4px 0 0",
                      fontWeight: 400,
                      fontSize: 24,
                      lineHeight: 1.2
                    }}
                    level={1}
                  >
                    {alertsOverviewData?.total ? (
                      <NumberFlow
                        value={animatedOverviewTotal}
                        prefix={filters.alertsType === "volume" ? "$" : ""}
                        format={
                          filters.alertsType === "volume"
                            ? {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }
                            : { maximumFractionDigits: 0 }
                        }
                      />
                    ) : (
                      "-"
                    )}
                  </Title>
                  {alertsOverviewData?.comparison?.total != null && (
                    <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
                      <NumberFlow
                        value={Number(alertsOverviewData.comparison.total) || 0}
                        prefix={filters.alertsType === "volume" ? "$" : ""}
                        format={
                          filters.alertsType === "volume"
                            ? {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }
                            : { maximumFractionDigits: 0 }
                        }
                      />{" "}
                      {alertsOverviewData?.comparison_period?.label || t("previous period")}
                    </Text>
                  )}
                </div>
              </Flex>
            </Col>
            <Col span={24}>
              <Card title="" className="card-chart-wrapper alerts-overview">
                {!alertsOverviewData ? (
                  <Spin spinning={alertsOverviewFetching}>
                    <Flex vertical justify="start" align="center" style={{ height: 254 }}>
                      <img
                        src={
                          Images?.[theme === "dark" ? "alerts_overview_dark" : "alerts_overview"]
                        }
                        alt=""
                        width={320}
                      />
                      <Text style={{ fontSize: 16 }}>No data available yet..</Text>
                      <Text
                        type="secondary"
                        style={{
                          textAlign: "center",
                          paddingTop: 8,
                          maxWidth: 500
                        }}
                      >
                        {`${alertText} ${t("You will be notified by email.")}`}
                      </Text>
                    </Flex>
                  </Spin>
                ) : (
                  <AlertsOverviewChart />
                )}
              </Card>
            </Col>
          </>
        )}
        {!isAdminPage && !isMobile && (
          <Col span={24}>
            <SankeySection flow={alertsFlowData} theme={theme} period={getDates(filters.period)} />
          </Col>
        )}
        <Col
          span={12}
          style={{
            width: isMobile && "100%",
            maxWidth: isMobile && "unset",
            flex: isMobile && "unset"
          }}
        >
          {isAdmin && !usersViewForAdmin ? (
            <Card
              title="Alerts by Business Account"
              className="card-chart-wrapper withTitle"
              styles={{ header: { border: "none" } }}
              style={{ minHeight: !isMobile && ALERTS_CARD_HEIGHT, height: "100%" }}
              extra={
                <div style={{ height: 24 }}>
                  <DropdownBlockCheckbox
                    id={"business_accounts"}
                    withSearch={true}
                    setDropdownFilters={handleChangeFilterAccounts}
                    dropdownFilters={filters.business_accounts}
                    allBtnText="All"
                    label="Accounts"
                    data={allBusinessAccountsList || []}
                    disabled={isAccountPending}
                    filterKey={"id"}
                  />
                </div>
              }
            >
              <CardTotalPanel
                count={alertsByAccountData?.total_count}
                volume={alertsByAccountData?.total_volume}
                isMobile={isMobile}
                {...compareProps(alertsByAccountData)}
              />

              <AlertsAccountChart />
            </Card>
          ) : (
            <Card
              title="Alerts by Descriptor"
              className="card-chart-wrapper withTitle alerts"
              styles={{ header: { border: "none" } }}
              style={{ minHeight: !isMobile && ALERTS_CARD_HEIGHT, height: "100%" }}
              extra={
                <div style={{ height: !isMobile && 24 }}>
                  <DropdownBlockCheckbox
                    id={"descriptors"}
                    withSearch={true}
                    setDropdownFilters={handleChangeFilterDescriptors}
                    dropdownFilters={filters.descriptors}
                    allBtnText="All"
                    label="Descriptors"
                    data={alertsByDescriptorData?.["all_descriptors"]}
                  />
                </div>
              }
            >
              <CardTotalPanel
                count={alertsByDescriptorData?.total_count}
                volume={alertsByDescriptorData?.total_volume}
                isMobile={isMobile}
                {...compareProps(alertsByDescriptorData)}
              />

              <AlertsDescriptorChart />
            </Card>
          )}
        </Col>
        <Col
          span={12}
          style={{
            width: isMobile && "100%",
            maxWidth: isMobile && "unset",
            flex: isMobile && "unset"
          }}
        >
          <Card
            title="Alerts by Country"
            className="card-chart-wrapper withTitle"
            styles={{ header: { border: "none" } }}
            style={{ minHeight: !isMobile && ALERTS_CARD_HEIGHT, height: "100%" }}
            extra={
              isAccountPending || isMobile ? (
                <span />
              ) : (
                <ButtonComponent
                  type={"link"}
                  onClick={() => openFullPage("alerts_by_country")}
                  icon={<Icon type={"chevron_right_small"} />}
                  iconPosition={"end"}
                  style={{ padding: 0 }}
                  size={"small"}
                >
                  {t("View All")}
                </ButtonComponent>
              )
            }
          >
            <CardTotalPanel
              count={alertsCountryData?.total_count}
              volume={alertsCountryData?.total_volume}
              isMobile={isMobile}
              {...compareProps(alertsCountryData)}
            />

            <AlertsCountryChart isMobile={isMobile} />

            {isMobile && (
              <ButtonComponent
                height="40"
                style={{ width: "100%", marginTop: 18 }}
                onClick={() => openFullPage("alerts_by_country")}
              >
                {t("View all")}
              </ButtonComponent>
            )}
          </Card>
        </Col>
        <Col
          span={12}
          style={{
            width: isMobile && "100%",
            maxWidth: isMobile && "unset",
            flex: isMobile && "unset"
          }}
        >
          <Card
            title={t("Alerts by Bank")}
            className="card-chart-wrapper withTitle"
            styles={{ header: { border: "none" } }}
            style={{ minHeight: !isMobile && ALERTS_CARD_HEIGHT, height: "100%" }}
            extra={
              isAccountPending || isMobile ? (
                <span />
              ) : (
                <ButtonComponent
                  type={"link"}
                  onClick={() => openFullPage("alerts_by_bank")}
                  icon={<Icon type={"chevron_right_small"} />}
                  iconPosition={"end"}
                  style={{ padding: 0 }}
                  size={"small"}
                >
                  {t("View All")}
                </ButtonComponent>
              )
            }
          >
            <CardTotalPanel
              count={alertsBankData?.total_count}
              volume={alertsBankData?.total_volume}
              isMobile={isMobile}
              {...compareProps(alertsBankData)}
            />

            <Table
              className={"chart-table"}
              dataSource={alertsBankData?.result}
              columns={alertsByBankColumns}
              onRow={getDashboardProgressRowProps}
              pagination={false}
              rowKey="label"
              tableLayout="fixed"
              showHeader={!!alertsBankData?.result?.length}
              locale={{
                emptyText: <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
              }}
            />
            {isMobile && (
              <ButtonComponent
                height="40"
                style={{ width: "100%", marginTop: 18 }}
                onClick={() => openFullPage("alerts_by_bank")}
              >
                {t("View all")}
              </ButtonComponent>
            )}
          </Card>
        </Col>
        <Col
          span={12}
          style={{
            width: isMobile && "100%",
            maxWidth: isMobile && "unset",
            flex: isMobile && "unset"
          }}
        >
          <Card
            title="Alerts by Card Type"
            className="card-chart-wrapper withTitle"
            styles={{ header: { border: "none" } }}
            style={{
              minHeight: !isMobile && ALERTS_CARD_HEIGHT,
              height: "100%",
              paddingBottom: isMobile && 40
            }}
          >
            <CardTotalPanel
              count={alertsCardTypeData?.total_count}
              volume={alertsCardTypeData?.total_volume}
              isMobile={isMobile}
              {...compareProps(alertsCardTypeData)}
            />

            <Table
              className={"chart-table"}
              dataSource={alertsCardTypeData?.result}
              columns={alertsByCardTypeColumns}
              onRow={getDashboardProgressRowProps}
              pagination={false}
              rowKey="label"
              tableLayout="fixed"
              showHeader={!!alertsCardTypeData?.result?.length && !isAccountPending}
              locale={{
                emptyText: <EmptyComponent type="no_data_available" />
              }}
            />
          </Card>
        </Col>
        {/* {(isAdmin && !usersViewForAdmin) && <Col span={12}>
          <Card
            title={t("Users by Source")}
            className="card-chart-wrapper withTitle"
            styles={{header: {border: 'none'}}}
            style={{minHeight: 704}}
            extra={
              isAccountPending ? <span/> : <ButtonComponent type={'link'}
                                                            onClick={() => openFullPage('users_by_source')}
                                                            icon={<Icon type={'chevron_right_small'}/>}
                                                            iconPosition={'end'}
                                                            style={{padding: 0}}
                                                            size={'small'}
              >
                {t('View All')}
              </ButtonComponent>}
          >
            <CardTotalPanel count={usersBySourceData?.total_count} showVolume={false}/>

            <Table
              className={"chart-table"}
              dataSource={usersBySourceData?.result}
              columns={usersBySourceTypeColumns}
              onRow={getDashboardProgressRowProps}
              pagination={false}
              rowKey="label"
              tableLayout="fixed"
              showHeader={!!usersBySourceData?.result?.length}
              locale={{emptyText: <EmptyComponent type="no_data_available" style={{minHeight: 450}}/>}}
            />

          </Card>
        </Col>} */}
        {!isAdminPage && (
          <Col span={24}>
            <CreditsSection
              summary={creditsSummaryData}
              theme={theme}
              isMobile={isMobile}
              loading={creditsSummaryFetching}
              onViewAll={() => navigate("/credits")}
            />
          </Col>
        )}
        <Col span={24}></Col>
        <TourComponent
          open={demoTourStep === 6 || (demoTourStep === 1 && showDemoTourDelayed)}
          steps={demoTourStep === 1 ? steps1 : steps6}
          onClose={() => props.setDemoTourStep(null)}
        />
      </Row>
    </div>
  )
}

const mapStateToProps = (state) => ({
  nav: state.nav,
  showModalPremiumFeatures: state.auth.showModalPremiumFeatures,
  isAdmin: state.auth.isAdmin,
  userInfo: state.auth.userInfo,
  dashboard: state.dashboard,
  statisticBlocksData: state.dashboard.statisticBlocksData,
  statisticLabels: state.dashboard.statisticLabels,
  statisticsFetching: state.dashboard.fetching,
  creditsSummaryData: state.dashboard.creditsSummaryData,
  creditsSummaryFetching: state.dashboard.creditsSummaryFetching,
  alertsFlowData: state.dashboard.alertsFlowData,
  businessAccount: state.user.businessAccount,
  inviteInfo: state.setting.inviteInfo,
  businessAccountsWithRole: state.user.businessAccountsWithRole,
  test_data: state.globalFilters.testData,
  balanceEnabled: state.auth.userInfo?.["balance_feature"],
  demoTourStep: state.user.demoTourStep,
  enrollments: state.user.enrollments
})

const mapDispatchToProps = (dispatch) => ({
  setShowModalPremiumFeatures: (value) => dispatch(AuthCreators.setShowModalPremiumFeatures(value)),
  getUserInfo: () => dispatch(UserCreators.userRequest()),
  getDashboardStatistics: (params) => dispatch(Creators.getDashboardStatisticsRequest(params)),
  getAlertsFlow: (params) => dispatch(Creators.getAlertsFlowRequest(params)),
  getCreditsSummary: (params) => dispatch(Creators.getCreditsSummaryRequest(params)),
  declinePremiumFeatures: () => dispatch(UserCreators.declinePremiumFeaturesRequest()),
  getAlertOverviewData: (params) => dispatch(Creators.getAlertOverviewDataRequest(params)),
  // getUsersBySourceData: (params) =>
  //   dispatch(Creators.getUsersBySourceDataRequest(params)),
  getAlertsDescriptorData: (params) => dispatch(Creators.getAlertsDescriptorDataRequest(params)),
  getAlertsAccountData: (params) => dispatch(Creators.getAlertsAccountDataRequest(params)),
  getAlertsCountryData: (params) => dispatch(Creators.getAlertsCountryDataRequest(params)),
  getAdminBusinessAccounts: (params) =>
    dispatch(BusinessAccounts.getAdminBusinessAccountsRequest(params)),
  getAlertsBankData: (params) => dispatch(Creators.getAlertsBankDataRequest(params)),
  getAlertsCardTypeData: (params) => dispatch(Creators.getAlertsCardTypeDataRequest(params)),
  getAllBusinessAccountsList: () => dispatch(Creators.getAllBusinessAccountsListRequest()),
  getBusinessAccount: (id) => dispatch(UserCreators.getBusinessAccountRequest(id)),
  changeFilters: (filters) => dispatch(Creators.dashboardSetProp("filters", filters)),

  setDemoTourStep: (step) => dispatch(UserCreators.setDemoTourStep(step)),
  getEnrollments: (businessAccountId) =>
    dispatch(UserCreators.getEnrollmentsRequest(businessAccountId))
})

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage)
